import { formatDate } from 'util/formatDate';
import React, { ReactElement, useEffect, useState } from 'react';
import { Box, Typography, Chip, Pagination, useMediaQuery } from '@mui/material';
import { useCoCurricularApi } from 'services/api/hooks';
import { Entry } from 'services/api/apis/co-curricular/dto/entries.dto';
import { theme } from 'theme';
import { SectionCountBadge } from './SectionCountBadge';
import { CoCurricularEmptyState } from './CoCurricularEmptyState';

const PAGE_SIZE = 10;

const LabeledField = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): ReactElement => (
  <Box>
    <Typography
      component="span"
      sx={{ fontSize: '12px', fontWeight: 600, color: '#6B6B6B', mr: '6px' }}
    >
      {label}
    </Typography>
    <Typography component="span" sx={{ fontSize: '14px', color: '#1F1F1F' }}>
      {children}
    </Typography>
  </Box>
);

// Mobile card matching the Figma mobile co-curricular design:
// date / event / org / Role / Duration / Skills / Approver / ✓ Verified.
const MobileEntryCard = ({ entry }: { entry: Entry }): ReactElement => (
  <Box
    sx={{
      border: '1px solid #E8E8E8',
      borderRadius: '12px',
      padding: '16px',
      backgroundColor: '#FFFFFF',
      marginBottom: '12px',
    }}
  >
    <Typography sx={{ fontSize: '12px', color: '#6B6B6B', marginBottom: '4px' }}>
      {formatDate(new Date(entry.startDate), { includeDate: true, includeYear: false })}
    </Typography>
    <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#1F1F1F' }}>
      {entry.eventName}
    </Typography>
    <Typography sx={{ fontSize: '13px', color: '#6B6B6B', marginBottom: '12px' }}>
      {entry.organizations.join(', ')}
    </Typography>

    <Box display="flex" flexWrap="wrap" gap="8px 24px" mb={12}>
      <LabeledField label="Role">{entry.role ?? 'Participant'}</LabeledField>
      <LabeledField label="Duration">{`${entry.eventHours.toFixed(1)} h`}</LabeledField>
    </Box>

    <Typography
      sx={{ fontSize: '12px', fontWeight: 600, color: '#6B6B6B', marginBottom: '6px' }}
    >
      Skills
    </Typography>
    <Box display="flex" flexWrap="wrap" gap="6px" mb={12}>
      {entry.naceCompetencies.map(comp => (
        <Chip
          key={comp}
          label={comp}
          size="small"
          sx={{
            fontSize: '11px',
            height: '24px',
            backgroundColor: '#E8E8E8',
            color: '#1F1F1F',
            border: 'none',
          }}
        />
      ))}
    </Box>

    {entry.approvers.length > 0 && (
      <Typography sx={{ fontSize: '13px', color: '#1F1F1F', marginBottom: '8px' }}>
        {entry.approvers.join(', ')}
      </Typography>
    )}

    {entry.status === 'verified' && (
      <Typography
        sx={{
          fontSize: '13px',
          fontWeight: 600,
          color: '#1B873F',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        ✓ Verified
      </Typography>
    )}
  </Box>
);

export const AllEntriesTable = ({
  variant = 'student',
}: {
  variant?: 'student' | 'admin';
}): ReactElement | null => {
  const coCurricularApi = useCoCurricularApi();
  const [page, setPage] = useState(1);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [count, setCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isAdmin = variant === 'admin';

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      const result = await coCurricularApi.getEntries(page);

      if (!cancelled && result.success) {
        setEntries(result.value.entries);
        setCount(result.value.count);
      }
      if (!cancelled) setLoading(false);
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [coCurricularApi, page]);

  useEffect(() => {
    let cancelled = false;

    const fetchSummary = async () => {
      setSummaryLoading(true);
      const result = await coCurricularApi.getSummary();

      if (!cancelled && result.success) setTotalCount(result.value.totalEvents);

      if (!cancelled) setSummaryLoading(false);
    };

    fetchSummary();

    return () => {
      cancelled = true;
    };
  }, [coCurricularApi]);

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  if (loading || summaryLoading) return null;

  if (totalCount === 0) {
    // The "how to earn entries" guide is written for the student; admins
    // viewing someone else's transcript should not see it.
    return isAdmin ? null : <CoCurricularEmptyState />;
  }

  // Admin view adds ROLE and STATUS columns to match the design:
  // DATE | EVENT | ROLE | HOURS | NACE COMPETENCIES | STATUS | APPROVER(S)
  const headerColumns = isAdmin
    ? '80px 2fr 1fr 1fr 2fr 1fr 1.5fr'
    : '80px 2fr 1fr 2fr 1.5fr';

  return (
    <Box sx={{ marginBottom: '48px' }}>
      <Box display="flex" alignItems="center" gap="16px" mb={2}>
        <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#1F1F1F' }}>
          All Entries
        </Typography>
        <SectionCountBadge count={totalCount} />
      </Box>
      <Typography
        sx={{
          fontSize: '14px',
          color: '#6B6B6B',
          marginBottom: '24px',
        }}
      >
        {isAdmin
          ? 'Every event this student attended or organized. Tags show the competencies each entry evidences.'
          : 'Every event you\'ve attended. Tags describe competencies earned.'}
      </Typography>

      {/* Desktop / tablet: grid table */}
      {!isMobile && (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: headerColumns,
              gap: '16px',
              padding: '12px 16px',
              backgroundColor: '#F9F9F9',
              borderRadius: '8px 8px 0 0',
              borderBottom: '1px solid #E8E8E8',
            }}
          >
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#6B6B6B' }}>
              DATE
            </Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#6B6B6B' }}>
              EVENT
            </Typography>
            {isAdmin && (
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#6B6B6B' }}>
                ROLE
              </Typography>
            )}
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#6B6B6B' }}>
              HOURS
            </Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#6B6B6B' }}>
              NACE COMPETENCIES
            </Typography>
            {isAdmin && (
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#6B6B6B' }}>
                STATUS
              </Typography>
            )}
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#6B6B6B' }}>
              APPROVER(S)
            </Typography>
          </Box>

          {!loading && entries.length === 0 && (
            <Box sx={{ padding: '24px 16px' }}>
              <Typography sx={{ fontSize: '14px', color: '#6B6B6B' }}>
                No entries yet.
              </Typography>
            </Box>
          )}
          {entries.map(entry => (
            <Box
              key={entry.eventId}
              sx={{
                display: 'grid',
                gridTemplateColumns: headerColumns,
                gap: '16px',
                padding: '16px',
                backgroundColor: '#FFFFFF',
                borderBottom: '1px solid #E8E8E8',
                '&:hover': {
                  backgroundColor: '#F9F9F9',
                },
              }}
            >
              <Typography sx={{ fontSize: '14px', color: '#1F1F1F' }}>
                {formatDate(new Date(entry.startDate), { includeDate: true, includeYear: false })}
              </Typography>
              <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1F1F1F', marginBottom: '4px' }}>
                  {entry.eventName}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#6B6B6B' }}>
                  {entry.organizations.join(', ')}
                </Typography>
              </Box>
              {isAdmin && (
                <Typography sx={{ fontSize: '14px', color: '#1F1F1F' }}>
                  {entry.role}
                </Typography>
              )}
              <Typography sx={{ fontSize: '14px', color: '#1F1F1F' }}>
                {entry.eventHours.toFixed(1)} h
              </Typography>
              <Box display="flex" flexWrap="wrap" gap="6px">
                {entry.naceCompetencies.map(comp => (
                  <Chip
                    key={comp}
                    label={comp}
                    size="small"
                    sx={{
                      fontSize: '11px',
                      height: '24px',
                      backgroundColor: '#E8E8E8',
                      color: '#1F1F1F',
                      border: 'none',
                    }}
                  />
                ))}
              </Box>
              {isAdmin && (
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#1B873F',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  ✓ Verified
                </Typography>
              )}
              <Typography sx={{ fontSize: '14px', color: '#1F1F1F' }}>
                {entry.approvers.join(', ')}
              </Typography>
            </Box>
          ))}
        </>
      )}

      {/* Mobile: card list (Figma mobile co-curricular design) */}
      {isMobile && (
        <Box>
          {entries.map(entry => (
            <MobileEntryCard key={entry.eventId} entry={entry} />
          ))}
          {!loading && entries.length === 0 && (
            <Typography sx={{ fontSize: '14px', color: '#6B6B6B', padding: '16px 0' }}>
              No entries yet.
            </Typography>
          )}
        </Box>
      )}

      {/* Pagination */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '24px',
        }}
      >
        <Typography sx={{ fontSize: '14px', color: '#6B6B6B' }}>
          Page: {page} of {totalPages}
        </Typography>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_event, value) => setPage(value)}
          shape="rounded"
          sx={{
            '& .MuiPaginationItem-root': {
              fontSize: '14px',
            },
          }}
        />
      </Box>
    </Box>
  );
};
